import axios from "axios";
import * as tokenStore from "./tokenStore";
import { FileTransfer } from "@capacitor/file-transfer";
import { Capacitor } from "@capacitor/core";
import { BASE_URL } from "./api";
import { REPORT_ENDPOINTS } from "./endpoints";
import { buildQueryParams } from "../utils/helpers";

/**
 * Reads the current auth token from storage.
 * ⚠️ On WEB there is no token to read. The kernel puts both tokens in HttpOnly cookies and
 * blanks them in the response body (HIPAA #6), so JS cannot see them by design — the cookie
 * authenticates the download itself. Returning null here is correct, not a failure; the
 * caller branches on platform.
 *
 * ⚠️ The previous comment here claimed "the apiClient interceptor keeps this token fresh
 * automatically on 401s, so no proactive refresh is needed before downloads." That was
 * never true — this path does not touch an interceptor. It only appeared true when some
 * other request happened to refresh first. On a 15-minute Cognito access token that
 * coincidence stops being reliable.
 *
 * Reads through tokenStore so there is one source (D28). Callers that make their own
 * request should prefer `authedFetch` from ./api, which carries the shared refresh.
 */
export const getAuthToken = async (): Promise<string | null> => {
  if (Capacitor.getPlatform() === "web") return null;
  const token = await tokenStore.getAccessToken();
  if (!token) throw new Error("No auth token found");
  return token;
};

export interface PdfReportParams {
  viewingProfileId: number | string;
  startDate: string;
  endDate: string;
  timeZone: string;
  refIds: string;
  graphType: string;
  showMinMax: boolean;
  userName: string;
  deviceType: "clear" | "cough";
}

/**
 * Downloads the PDF therapy report on native platforms (iOS/Android) via FileTransfer.
 */
export const downloadPdfReportNative = async (
  params: PdfReportParams,
  token: string,
  destinationUri: string,
): Promise<{ path?: string }> => {
  try {
    const query = buildQueryParams({
      viewing_profile_id: params.viewingProfileId,
      startDate: params.startDate,
      endDate: params.endDate,
      timeZone: params.timeZone,
      refIds: params.refIds,
      graphType: params.graphType,
      showMinMax: params.showMinMax,
      userName: params.userName,
    });

    const endpoint =
      params.deviceType === "clear"
        ? REPORT_ENDPOINTS.PEP_PDF
        : REPORT_ENDPOINTS.MIE_PDF;

    const url = `${BASE_URL}${endpoint}?${query}`;

    const result = await FileTransfer.downloadFile({
      url,
      path: destinationUri,
      headers: { Authorization: `Bearer ${token}` },
      progress: true,
    });

    if (!result.path) {
      throw new Error("Download failed: No file path returned");
    }

    return result;
  } catch (error) {
    console.error("Native PDF download error:", error);
    throw new Error(`Failed to download PDF report: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Downloads the PDF therapy report on web as a Blob.
 * On web, auth is handled by HttpOnly cookies (withCredentials) so token may be null.
 * @param onProgress Optional callback receiving download percentage (0-100).
 */
export const downloadPdfReportWeb = async (
  params: PdfReportParams,
  token: string | null,
  onProgress?: (percent: number) => void,
): Promise<Blob> => {
  try {
    const query = buildQueryParams({
      viewing_profile_id: params.viewingProfileId,
      startDate: params.startDate,
      endDate: params.endDate,
      timeZone: params.timeZone,
      refIds: params.refIds,
      graphType: params.graphType,
      showMinMax: params.showMinMax,
      userName: params.userName,
    });

    const endpoint =
      params.deviceType === "clear"
        ? REPORT_ENDPOINTS.PEP_PDF
        : REPORT_ENDPOINTS.MIE_PDF;

    const url = `${BASE_URL}${endpoint}?${query}`;

    const response = await axios.get(url, {
      responseType: "blob",
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          onProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        }
      },
    });

    if (!response.data) {
      throw new Error("Download failed: No data received");
    }

    return response.data;
  } catch (error) {
    console.error("Web PDF download error:", error);
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || error.message;
      throw new Error(`Failed to download PDF report: ${message}`);
    }
    throw new Error(`Failed to download PDF report: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};
